import React, {useEffect, useState} from "react";
//import React, {useContext, useEffect, useState} from "react";
import axios from "axios";
import {useAuth} from "../useAuth";
import {GenericObject} from "./objects/GenericObject";
import {DataTable} from "primereact/datatable";
import {Column} from "primereact/column";
import {Dialog} from "primereact/dialog";
import {InputText} from "primereact/inputtext";
import {Button} from "primereact/button";
import {Panel} from "primereact/panel";
import {ConfirmPopup, confirmPopup} from 'primereact/confirmpopup';
import {Meta} from "./objects/Meta";
import {fetchObjects, createObject, deleteFromObjectsGrid} from "../services/CRUD";

interface DataObject {
    deleted: boolean;
    id: string;
    meta: Meta;
    namespace: string;
    objectID: string;
    objectType: string;
    stringAttributes: { name: string; val: string }[];
    tags: { name: string; val: string }[];
}

const GenericObjects = () => {

    const {ogToken, ogAccessPoint} = useAuth();

    const [genericObjects, setGenericObjects] = useState([]);
    const [newObjectDialogVisible, setNewObjectDialogVisible] = useState(false);

    /* Fields on new object form */
    const [bespokeObjectId, setBespokeObjectId] = useState('');
    const [objectType, setObjectType] = useState('');
    const [namespace, setNamespace] = useState('');

    const [nameValuePairs, setNameValuePairs] = useState<{ name: string, val: string }[]>([]);
    const [tagNameValuePairs, setTagNameValuePairs] = useState<{ name: string, val: string }[]>([]);

    /* Fields used in the editing functionality */
    const [isEditMode, setIsEditMode] = useState(false);
    const [selectedObjectId, setSelectedObjectId] = useState<string | null>(null);

    const [showObjectTypeValidation, setShowObjectTypeValidation] = useState(false);

    /* A popup to show the user a command to get thier generic objects from a terminal */
    const [showCurlDialog, setShowCurlDialog] = useState(false);

    const objectsGridGenericObjectsUrl = process.env.REACT_APP_OBJECTS_GRID_BASE_URL + '/genericobjects/';

    useEffect(() => {
        fetchGenericObjects();
    }, [])

    const fetchGenericObjects = async () => {
        fetchObjects(objectsGridGenericObjectsUrl + "?from=0&size=1000",
            ogAccessPoint,
            ogToken)
            .then(responseArray => {
                // console.log("fetchObjects response: ", responseArray);
                setGenericObjects(responseArray || []);
            }).catch(error => {
                console.error('Fetching generic objects failed:', error);
                setGenericObjects([]);
        });
    }

    const createGenericObject = async () => {

        setShowObjectTypeValidation(false);
        if (!objectType) {
            console.error('Object Type is required');
            setShowObjectTypeValidation(true);
            return;
        }

        const newGenericObject: GenericObject = {
            // id: "generated-id-123",
            objectID: bespokeObjectId,
            objectType: objectType,
            namespace: namespace,
            stringAttributes: nameValuePairs.map(pair => ({
                name: pair.name,
                val: pair.val,
            })),
            tags: tagNameValuePairs.map(pair => ({
                name: pair.name,
                val: pair.val,
            }))
        };

         createObject(objectsGridGenericObjectsUrl,
                ogAccessPoint,
                ogToken,
                newGenericObject)
             .then(response => {
                setNewObjectDialogVisible(false);
                console.log("New GenericObject created. Refreshing...");
                fetchGenericObjects();
         }).catch(error => console.error('Adding generic object failed:', error));

    }

    // Helper function to extract stringAttributes
    const renderStringAttributes = (rowData: DataObject) => {
        return rowData.stringAttributes.map(attr => (
            <div key={attr.name}>{`${attr.name}: ${attr.val}`}</div>
        ));
    };

    const renderTags = (rowData: DataObject) => {
        return rowData.tags.map(attr => (
            <div key={attr.name}>{`${attr.name}: ${attr.val}`}</div>
        ));
    };

    // Add new name-value pair
    const addNameValuePair = () => {
        setNameValuePairs([...nameValuePairs, {name: '', val: ''}]);
    };

    // Update name-value pair
    const updateNameValuePair = (index: number, field: 'name' | 'val', value: string) => {
        const updatedPairs = [...nameValuePairs];
        updatedPairs[index][field] = value;
        setNameValuePairs(updatedPairs);
    };

    // Remove name-value pair
    const removeNameValuePair = (index: number) => {
        const updatedPairs = nameValuePairs.filter((_, i) => i !== index);
        setNameValuePairs(updatedPairs);
    };

    // Add new name-value pair
    const addTagNameValuePair = () => {
        setTagNameValuePairs([...tagNameValuePairs, {name: '', val: ''}]);
    };

    // Update name-value pair
    const updateTagNameValuePair = (index: number, field: 'name' | 'val', value: string) => {
        const updatedPairs = [...tagNameValuePairs];
        updatedPairs[index][field] = value;
        setTagNameValuePairs(updatedPairs);
    };

    // Remove name-value pair
    const removeTagNameValuePair = (index: number) => {
        const updatedPairs = tagNameValuePairs.filter((_, i) => i !== index);
        setTagNameValuePairs(updatedPairs);
    };

    const tagsPanelHeader = (options: { className: any; }) => {
        const className = `${options.className} justify-content-space-between`;

        return (
            <div className={className}>
                <div className="flex align-items-center gap-2">
                    <span className="font-bold">Tags [Optional]</span>
                </div>
                <div>
                    <Button
                        icon="pi pi-plus"
                        label="Add New Tag"
                        onClick={addTagNameValuePair}
                    />
                </div>
            </div>
        );
    };

    const stringAttributesPanelHeader = (options: { className: any; }) => {
        const className = `${options.className} justify-content-space-between`;

        return (
            <div className={className}>
                <div className="flex align-items-center gap-2">
                    <span className="font-bold">String Attributes</span>
                </div>
                <div>
                    <Button
                        icon="pi pi-plus"
                        label="Add New Pair"
                        onClick={addNameValuePair}
                    />
                </div>
            </div>
        );
    };

    const handleEdit = (row: DataObject) => {
        // Open edit modal or navigate, etc.
        console.log('Edit', row);

        setIsEditMode(true);
        setSelectedObjectId(row.id);
        setBespokeObjectId(row.objectID);
        setObjectType(row.objectType);
        setNamespace(row.namespace);
        setNameValuePairs(row.stringAttributes);
        setTagNameValuePairs(row.tags);
        setNewObjectDialogVisible(true); // show dialog
    };

    const updateGenericObject = async () => {
        console.log("Update Object...");
        const updatedObject: GenericObject = {
            id: selectedObjectId!,
            objectID: bespokeObjectId,
            objectType: objectType,
            namespace: namespace,
            stringAttributes: nameValuePairs.map(pair => ({
                name: pair.name,
                val: pair.val,
            })),
            tags: tagNameValuePairs.map(pair => ({
                name: pair.name,
                val: pair.val,
            })),
        };

        const url = objectsGridGenericObjectsUrl + selectedObjectId;
        console.log(url);
        console.log('Updating object:', updatedObject);

        return axios.put(url,
            updatedObject, {
                headers: {
                    'Content-Type': 'application/json',
                    'X-ObjectsGrid-AccessPoint': ogAccessPoint,
                    'Authorization': `Bearer ${ogToken}`
                }
            }).then(response => {
            console.log('Updated successfully:', response.data);
            setNewObjectDialogVisible(false);
            fetchGenericObjects();
        }).catch(error => console.error('Updating object failed:', error));
    };

    const newGenericObjectDialogFooterContent = (
        <div>
            <Button label="Cancel"
                    icon="pi pi-times"
                    onClick={() => setNewObjectDialogVisible(false)}
                    className="p-button-text"/>
            <Button
                label={isEditMode ? "Update" : "Create"}
                icon="pi pi-check"
                onClick={isEditMode ? updateGenericObject : createGenericObject}
                autoFocus
            />
        </div>
    );

    const closeDialog = () => {
        setNewObjectDialogVisible(false);
        setIsEditMode(false);
        setSelectedObjectId(null);
        setBespokeObjectId('');
        setObjectType('');
        setNamespace('');
        setNameValuePairs([]);
        setTagNameValuePairs([]);
    };

    const handleDelete = (row: DataObject, event: React.MouseEvent<HTMLButtonElement>) => {

        confirmPopup({
            target: event.currentTarget,
            message: 'Do you want to delete this Generic Object?',
            icon: 'pi pi-info-circle',
            defaultFocus: 'reject',
            acceptClassName: 'p-button-danger',
            accept: () => {
                console.log('Delete', row);
                deleteGenericObjectFromObjectsGrid(row).then((response) => {
                    console.log('Response from delete:', response);
                    fetchGenericObjects();
                })
            },
            reject: () => {
                console.log('Cancel delete');
            }
        })
    };

    const deleteGenericObjectFromObjectsGrid = async (row: DataObject) => {

        deleteFromObjectsGrid(objectsGridGenericObjectsUrl + row.id,
            ogAccessPoint,
            ogToken)
            .then(response => {
                console.log('Deleted successfully:', response);

                // We to wait a couple of seconds here. It takes OG a bit to delete the object. It's not pretty
                // but it is necessary.
                setTimeout(() => {
                    console.log('Refreshing Generic Objects after delete...');
                    fetchGenericObjects();
                }, 2000);

            })
            .catch(error => {
               console.error('DeleteFailed:', error);
            });
    }

    const renderActions = (rowData: DataObject) => {
        return (
            <div className="flex gap-2">
                <Button icon="pi pi-pencil" rounded text aria-label="Edit" onClick={() => handleEdit(rowData)}/>
                <Button icon="pi pi-times" rounded text severity="danger" aria-label="Delete"
                        onClick={(e) => handleDelete(rowData, e)}/>
            </div>
        );
    };

    const genericObjectsCurlDialogHeader = (options: { className: any; }) => {
        const className = `${options.className} justify-content-space-between`;

        return (
            <div className={className}>
                <div className="flex align-items-center gap-2">
                    <span className="font-bold text-lg">Generic Objects</span>
                </div>
                <div>
                    <Button
                        icon="pi pi-code"
                        label="Show cURL"
                        onClick={() => setShowCurlDialog(true)}
                    />
                </div>
            </div>
        );
    };

    const curlCommand = `curl -X GET '${objectsGridGenericObjectsUrl}' \\
                  -H "Authorization: Bearer ${ogToken}" \\
                  -H "X-ObjectsGrid-AccessPoint: ${ogAccessPoint}"`;

    return (
        <div>
            <Panel headerTemplate={genericObjectsCurlDialogHeader}>
                <Button
                    label="Create GenericObject"
                    icon="pi pi-plus"
                    className="mb-2 mr-2"
                    onClick={() => {
                        setIsEditMode(false);
                        setSelectedObjectId(null);
                        setBespokeObjectId('');
                        setObjectType('');
                        setNamespace('');
                        setNameValuePairs([]);
                        setTagNameValuePairs([]);
                        setNewObjectDialogVisible(true);
                    }}
                />
                <Button label="Refresh" icon="pi pi-refresh" className="mb-2" onClick={fetchGenericObjects}/>
                <div className="datatable-container">
                    <DataTable value={genericObjects}
                               paginator
                               stripedRows
                               rows={100}
                                emptyMessage="No Generic Objects were found">
                        {/* Render columns */}
                        <Column field="id" header="ID"/>
                        <Column field="objectID" header="objectID"/>
                        <Column field="objectType" header="Object Type"/>
                        <Column field="namespace" header="Namespace"/>
                        <Column header="String Attributes" body={renderStringAttributes}/>
                        <Column field="tags" header="Tags" body={renderTags}/>
                        <Column field="meta.createdAt" header="Created At"/>
                        <Column field="meta.createdBy" header="Created By"/>
                        <Column header="Actions" body={renderActions} style={{width: '120px'}}/>
                    </DataTable>
                </div>

                <Dialog
                    header="Create New GenericObject"
                    visible={newObjectDialogVisible}
                    style={{ width: '50vw', maxWidth: '100vw', maxHeight: '100vh' }}
                    breakpoints={{ '960px': '90vw', '640px': '100vw' }}
                    contentStyle={{ maxHeight: '80vh', overflowY: 'auto' }}
                    onHide={closeDialog}
                    footer={newGenericObjectDialogFooterContent}
                    modal
                >
                    <div className="p-fluid">

                        <div className="p-field mb-2">
                            <label htmlFor="objecttype">Object Type [Required]</label>
                            <InputText
                                id="objecttype"
                                value={objectType}
                                onChange={(e) => setObjectType(e.target.value)}
                                required
                            />
                            {showObjectTypeValidation && <small className="p-error">Object Type is required</small>}
                        </div>

                        <div className="p-field mb-2">
                            <label htmlFor="bespokeObjectId">Bespoke Object ID [Optional]</label>
                            <InputText
                                id="bespokeObjectId"
                                value={bespokeObjectId}
                                onChange={(e) => setBespokeObjectId(e.target.value)}
                            />
                        </div>

                        <div className="p-field mb-2">
                            <label htmlFor="namespace">Namespace [Optional]</label>
                            <InputText
                                id="namespace"
                                value={namespace}
                                onChange={(e) => setNamespace(e.target.value)}
                            />
                        </div>

                        <Panel headerTemplate={stringAttributesPanelHeader} className="mb-2">
                            {nameValuePairs.map((pair, index) => (
                                <div key={index} className="p-d-flex p-ai-center p-jc-between p-mb-2 mb-2">
                                    <InputText
                                        placeholder="Name"
                                        value={pair.name}
                                        onChange={(e) => updateNameValuePair(index, 'name', e.target.value)}
                                        style={{marginRight: '1rem', width: '150px'}}
                                    />
                                    <InputText
                                        placeholder="Value"
                                        value={pair.val}
                                        onChange={(e) => updateNameValuePair(index, 'val', e.target.value)}
                                        style={{marginRight: '1rem', width: '150px'}}
                                    />
                                    <Button
                                        icon="pi pi-times"
                                        className="p-button-danger p-button-outlined"
                                        onClick={() => removeNameValuePair(index)}
                                    />
                                </div>
                            ))}
                        </Panel>

                        <Panel headerTemplate={tagsPanelHeader} className="mb-2">
                            {tagNameValuePairs.map((pair, index) => (
                                <div key={index} className="p-d-flex p-ai-center p-jc-between p-mb-2 mb-2">
                                    <InputText
                                        placeholder="Name"
                                        value={pair.name}
                                        onChange={(e) => updateTagNameValuePair(index, 'name', e.target.value)}
                                        style={{marginRight: '1rem', width: '150px'}}
                                    />
                                    <InputText
                                        placeholder="Value"
                                        value={pair.val}
                                        onChange={(e) => updateTagNameValuePair(index, 'val', e.target.value)}
                                        style={{marginRight: '1rem', width: '150px'}}
                                    />
                                    <Button
                                        icon="pi pi-times"
                                        className="p-button-danger p-button-outlined"
                                        onClick={() => removeTagNameValuePair(index)}
                                    />
                                </div>
                            ))}
                        </Panel>

                    </div>
                </Dialog>

                <ConfirmPopup/>

            </Panel>
            <Dialog
                header="cURL Command"
                visible={showCurlDialog}
                style={{width: '60vw'}}
                onHide={() => setShowCurlDialog(false)}
            >
                <div>
                    <p>You can use this command to get your GenericObjects from a terminal...</p>
                    <pre className="bg-gray-100 p-3 rounded overflow-auto">
                                <code>{curlCommand}</code>
                            </pre>
                    <Button
                        label="Copy to Clipboard"
                        icon="pi pi-copy"
                        onClick={() => navigator.clipboard.writeText(curlCommand)}
                        className="mt-2"
                    />
                </div>
            </Dialog>
        </div>);

};

export default GenericObjects;
